import { Action, createReducer, on } from '@ngrx/store';
import { createProfileReportFinished } from '@capital-access/common/actions';
import { LoadStatus } from '@capital-access/common/utils';
import {
  ActivityHistoryCount,
  createBnyManagementReport,
  createBnyManagementReportFinished,
  createConfigurableItineraryReport,
  createConfigurableItineraryReportFinished,
  createConfigurableProfileReport,
  createOwnershipAnalysisReport,
  createOwnershipAnalysisReportFinished,
  loadSelectedItemsFromStorage,
  loadSelectedTimeFromStorage,
  ReportSectionConfigForDraggableItem,
  ReportSectionConfigurationBase,
  ReportSectionConfigurationWithOrder,
  ReportViewModelSection,
  resetZoomValue,
  setZoomValue,
  sortActivitiesByDate,
  TemplateDesignerSelectedReportState,
  TemplateDesignerState,
  TemplateExportInitialState,
  TemplateExportListItemWithComments,
  TemplateExportState
} from '@capital-access/reporting/common';
import { getTemplateDesignerSelectedReportState } from '../services/configuration-panel.service';
import { ConfigurationPanelServiceResolver } from '../utils/configuration-panel-service.resolver';
import { getModifiedReportSection, getReportSectionConfig } from '../utils/template-designer.utils';
import {
  changeItineraryTab,
  clearErrorMessage,
  clearTemplateDesignerState,
  getTemplateDetailsById,
  getTemplateDetailsByIdFailed,
  goBackToReportConfiguration,
  initialUpdateReportSections,
  itineraryReportSectionSelectedViaCanvas,
  onEmptySelectionAlert,
  onHideExportWarning,
  onHideSelectWarning,
  onItineraryReportSectionCanvasChanged,
  onReportSectionCanvasChanged,
  onReportSectionCheckboxToggle,
  onReportSectionConfigAdditionalDataChanged,
  onReportSectionConfigCheckboxToggleFromDetailsPage,
  onReportSectionConfigDataPointToggle,
  onReportSectionDataPointSelectClearAll,
  onReportSectionOrderChanged,
  onReportSectionWidthChanged,
  reportSectionClearAll,
  reportSectionSelectAll,
  reportSectionSelected,
  reportSectionSelectedViaCanvas,
  saveTemplateDetails,
  saveTemplateDetailsSuccess,
  setCombinedOwnershipEnabled,
  setNotesFeatureEnabled,
  toggleSelectionByParticipant,
  updateActivityHistoryCount,
  updateReportData
} from './actions';
import {
  addExportItemToList,
  addRemoveItineraryToList,
  changeItinerarySelectedItem,
  changeSelectedItem,
  clearExportState,
  filterActivityApplySuccess,
  formatListForTemplateExportSuccess,
  getListItemsBySelectedListId,
  onCommentsSaveSuccess,
  onLoadCompanyContacts,
  onLoadCompanyContactSuccess,
  onOwnershipAnalysisCommentsSaveSuccess,
  onPeerOwnershipCommentsSaveSuccess,
  openCommentSectionDrawerForSelectedItem,
  openExportListDrawer,
  openFilterActivityDrawer,
  openPeerOwnershipCommentSectionDrawer,
  removeExportItemFromList,
  setExportList,
  setExportSucceeded,
  setIncludeOwnershipAnalysis,
  setItineraryComments,
  setOwnershipAnalysisExportList,
  setOwnershipAnalysisPeers,
  setPeerOwnershipExportList,
  setSelectedBnyDate,
  setSelectedBnySecurityId,
  setSelectedOwnershipAnalysisSecurityId,
  setSelectedPeerOwnershipSecurityId,
  setTemplateExportData
} from './export/export-actions';
export const TemplateZoomInitialState = {
  zoom: 1,
  previousZoom: null
};
export const initialState: TemplateDesignerState = {
  reportTemplate: null,
  reportSections: [],
  initialReportSections: [],
  formattedSections: [],
  reportData: {},
  sampleData: true,
  loadingState: LoadStatus.Pristine,
  savingState: LoadStatus.Pristine,
  selectedReport: null,
  error: null,
  reportExportError: null,
  activityHistoryCount: ActivityHistoryCount.Five,
  isSelectionByParticipant: false,
  export: TemplateExportInitialState,
  showEmptySelectionAlert: false,
  showCombinedOwnership: false,
  showNotesFeature: false,
  getDetailsError: null,
  initial: false,
  zoomState: TemplateZoomInitialState,
  selectedItineraryTab: 'events'
};
const reducer = createReducer(
  initialState,
  on(initialUpdateReportSections, (state, { reportTemplate, reportSections, draggableItems, initial }) => ({
    ...state,
    reportTemplate,
    reportSections,
    initialReportSections: reportSections,
    formattedSections: draggableItems,
    loadingState: LoadStatus.Loaded,
    initial: initial
  })),
  on(setZoomValue, (state, { zoom, savePrevious }) => ({
    ...state,
    zoomState: {
      ...state.zoomState,
      previousZoom: savePrevious ? state.zoomState.zoom : state.zoomState.previousZoom,
      zoom
    }
  })),
  on(resetZoomValue, state => ({
    ...state,
    zoomState: {
      ...state.zoomState,
      zoom: state.zoomState.previousZoom ?? state.zoomState.zoom,
      previousZoom: null
    }
  })),
  on(reportSectionSelected, (state, { selectedReport }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const selectedName = selectedReport?.name;

    const updatedSections = updateActiveTemplateFlags(state.reportSections, selectedName);

    let resolvedSelectedReport: TemplateDesignerSelectedReportState | null = null;
    if (selectedReport) {
      const resolvedSection = getReportSectionConfig(updatedSections, selectedName ?? '');
      if (resolvedSection) {
        resolvedSelectedReport = getTemplateDesignerSelectedReportState(resolvedSection);
      } else {
        resolvedSelectedReport = panelService.FindSelectedReportSectionConfig(
          updatedSections,
          selectedReport as ReportSectionConfigForDraggableItem
        );
      }
    }

    return {
      ...state,
      reportSections: updatedSections,
      selectedReport: resolvedSelectedReport
    };
  }),

  on(reportSectionSelectedViaCanvas, (state, { selectedReport }) => {
    const updatedSections = state.reportSections.map(section => {
      const updatedNestedItems = section.config.nestedItems?.map(nested => {
        if (!nested.nestedItemSectionConfiguration) return nested;
        return {
          ...nested,
          nestedItemSectionConfiguration: {
            ...nested.nestedItemSectionConfiguration,
            isActiveTemplate: nested.name === selectedReport?.config?.name
          }
        };
      });

      return {
        ...section,
        isActiveTemplate: section.config.name === selectedReport?.config?.name,
        config: {
          ...section.config,
          nestedItems: updatedNestedItems ?? section.config.nestedItems
        }
      } as typeof section;
    });

    const resolved = getTemplateDesignerSelectedReportState(
      getReportSectionConfig(state.reportSections, selectedReport?.config.name ?? '')
    );
    return {
      ...state,
      reportSections: updatedSections,
      selectedReport: resolved
    };
  }),

  on(itineraryReportSectionSelectedViaCanvas, (state, { selectedReport }) => {
    const updatedSections = state.reportSections.map(section => {
      const updatedNestedItems = section.config.nestedItems?.map(nested => {
        if (!nested.nestedItemSectionConfiguration) return nested;
        return {
          ...nested,
          nestedItemSectionConfiguration: {
            ...nested.nestedItemSectionConfiguration,
            isActiveTemplate: nested.name === selectedReport?.config?.name
          }
        };
      });

      return {
        ...section,
        isActiveTemplate: section.config.name === selectedReport?.config?.name,
        config: {
          ...section.config,
          nestedItems: updatedNestedItems ?? section.config.nestedItems
        }
      } as typeof section;
    });

    const resolved = getTemplateDesignerSelectedReportState(
      getReportSectionConfig(state.reportSections, selectedReport?.config.name ?? '')
    );
    return {
      ...state,
      reportSections: updatedSections,
      selectedReport: resolved
    };
  }),

  on(goBackToReportConfiguration, state => {
    const clearedSections = updateActiveTemplateFlags(state.reportSections, null);

    return {
      ...state,
      reportSections: clearedSections,
      selectedReport: null
    };
  }),
  on(onReportSectionCheckboxToggle, (state, { reportSection, checked }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(
      checked,
      [...state.reportSections],
      reportSection.name
    );

    if (!checked) {
      const uncheckedSection = modifiedReportSections.find(s => s.config.name === reportSection.name);
      if (uncheckedSection?.isActiveTemplate) {
        uncheckedSection.isActiveTemplate = false;
      }
    }

    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionConfigCheckboxToggleFromDetailsPage, (state, { reportSection, checked }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(
      checked,
      [...state.reportSections],
      reportSection.name
    );
    const modifiedReportSectionConfig = getReportSectionConfig(modifiedReportSections, reportSection?.name);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: {
        reportSectionConfig: modifiedReportSectionConfig!,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSectionConfig!)
      }
    };
  }),
  on(clearErrorMessage, state => ({
    ...state,
    error: null
  })),
  on(onHideExportWarning, state => ({
    ...state,
    export: {
      ...state.export,
      showExportWarning: false
    }
  })),
  on(onHideSelectWarning, state => ({
    ...state,
    export: {
      ...state.export,
      showSelectWarning: false
    }
  })),
  on(onReportSectionConfigDataPointToggle, (state, { dataSection, checked }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSection = panelService.UpdateReportSectionConfigOnDataSectionToggle(
      dataSection,
      checked,
      state.selectedReport?.reportSectionConfig as ReportSectionConfigurationWithOrder
    );
    const modifiedReportSections = getModifiedReportSection([...state.reportSections], modifiedReportSection);
    return {
      ...state,
      reportSections: modifiedReportSections,
      selectedReport: {
        reportSectionConfig: modifiedReportSection,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSection)
      },
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionDataPointSelectClearAll, (state, { selectAll }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSection = panelService.ReportSectionDataPointSelectClearAll(
      selectAll,
      state.selectedReport?.reportSectionConfig as ReportSectionConfigurationWithOrder
    );
    const modifiedReportSections = getModifiedReportSection([...state.reportSections], modifiedReportSection);
    return {
      ...state,
      reportSections: modifiedReportSections,
      selectedReport: {
        reportSectionConfig: modifiedReportSection,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSection)
      },
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionWidthChanged, (state, { reportSection, width }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const selectedSection = getReportSectionConfig(state.reportSections, reportSection.name)!;
    if (panelService.IsReportSectionConfigWidthValid(width, selectedSection)) {
      const modifiedReportSections = [...state.reportSections].map(section => {
        if (section.config.name === reportSection.name) {
          return { ...section, width: width } as ReportSectionConfigurationWithOrder;
        } else {
          const nestedMatch = section.config.nestedItems?.find(nested => nested.name === reportSection.name);
          if (nestedMatch && nestedMatch.nestedItemSectionConfiguration) {
            const updatedNestedItemSectionConfiguration = {
              ...nestedMatch.nestedItemSectionConfiguration,
              width: width
            };
            const updatedNestedItems = section.config.nestedItems?.map(nested =>
              nested.name === reportSection.name
                ? { ...nested, nestedItemSectionConfiguration: updatedNestedItemSectionConfiguration }
                : nested
            );
            return {
              ...section,
              config: {
                ...section.config,
                nestedItems: updatedNestedItems
              }
            } as ReportSectionConfigurationWithOrder;
          }
        }
        return section as ReportSectionConfigurationWithOrder;
      });
      const updatedSelectedReport =
        state.selectedReport?.reportSectionConfig.config.name === reportSection.name
          ? {
              ...state.selectedReport,
              reportSectionConfig: {
                ...state.selectedReport.reportSectionConfig,
                width: width
              }
            }
          : state.selectedReport;
      return {
        ...state,
        reportSections: modifiedReportSections,
        formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
        selectedReport: updatedSelectedReport
      };
    }
    return {
      ...state,
      formattedSections: panelService.ConvertReportSectionsToDraggableList([...state.reportSections]),
      error: panelService.GetWidthErrorMessage(selectedSection)
    };
  }),
  on(onReportSectionOrderChanged, (state, { draggableItems }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.UpdateReportSectionOrder(
      [...draggableItems],
      [...state.reportSections]
    );

    const updatedSelectedReport = state.selectedReport
      ? (() => {
          const updatedSection = modifiedReportSections.find(
            section => section.config.name === state.selectedReport?.reportSectionConfig.config.name
          );
          if (updatedSection) {
            const draggableItem = panelService.PrepareDraggableItemsConfig(updatedSection);
            const mutableDraggableItem = draggableItem.map(item => ({
              ...item,
              nestedItems: item.nestedItems ? Array.from(item.nestedItems) : undefined
            }));
            return {
              reportSectionConfig: updatedSection,
              reportSectionConfigDraggableItem: mutableDraggableItem
            };
          }
          return state.selectedReport;
        })()
      : null;

    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: updatedSelectedReport
    };
  }),
  on(onReportSectionCanvasChanged, (state, { changedReportSections }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const selectedReport = state.selectedReport?.reportSectionConfig.config.name;
    const changedSectionsMap = new Map(
      changedReportSections.map((section, index) => [section.config.name, { ...section, order: index }])
    );
    const sortedChangedSections = [...changedSectionsMap.values()];
    let changedIndex = 0;
    const modifiedReportSections = state.reportSections.map((original, index) => {
      if (changedSectionsMap.has(original.config.name)) {
        return { ...sortedChangedSections[changedIndex++], order: index };
      }
      return original;
    });
    const updatedSelectedReport = modifiedReportSections.find(section => section.config.name === selectedReport);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: updatedSelectedReport
        ? {
            reportSectionConfig: updatedSelectedReport,
            reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(updatedSelectedReport)
          }
        : state.selectedReport
    };
  }),
  on(onItineraryReportSectionCanvasChanged, (state, { changedReportSections }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const selectedReport = state.selectedReport?.reportSectionConfig.config.name;
    const changedSectionsMap = new Map(changedReportSections.map(section => [section.config.name, section]));

    const modifiedReportSections = state.reportSections.map(original => {
      let updatedSection = original;

      if (changedSectionsMap.has(original.config.name)) {
        const changedSection = changedSectionsMap.get(original.config.name)!;
        updatedSection = {
          ...changedSection,
          order: original.order
        };
      }

      if (updatedSection.config.nestedItems && updatedSection.config.nestedItems.length > 0) {
        const updatedNestedItems = updatedSection.config.nestedItems.map(nested => {
          if (changedSectionsMap.has(nested.name)) {
            const changedNested = changedSectionsMap.get(nested.name)!;
            return {
              ...nested,
              nestedItemSectionConfiguration: {
                ...nested.nestedItemSectionConfiguration,
                width: changedNested.width,
                // Preserve original nested item order during canvas changes (width adjustments)
                // Order updates from canvas interactions are handled by UpdateReportSectionOrder in service
                order: nested.nestedItemSectionConfiguration?.order
              }
            };
          }
          return nested;
        });

        updatedSection = {
          ...updatedSection,
          config: {
            ...updatedSection.config,
            nestedItems: updatedNestedItems
          }
        } as ReportSectionConfigurationWithOrder;
      }

      return updatedSection;
    });
    modifiedReportSections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    let updatedSelectedReport = modifiedReportSections.find(section => section.config.name === selectedReport);
    if (!updatedSelectedReport) {
      // check in nested items
      modifiedReportSections
        .filter(x => x.config.nestedItems)
        .forEach(section => {
          section.config.nestedItems?.forEach(nested => {
            if (nested.name === selectedReport && nested.nestedItemSectionConfiguration) {
              updatedSelectedReport = nested.nestedItemSectionConfiguration;
            }
          });
        });
    }

    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections),
      selectedReport: updatedSelectedReport
        ? {
            reportSectionConfig: updatedSelectedReport,
            reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(updatedSelectedReport)
          }
        : state.selectedReport
    };
  }),
  on(updateActivityHistoryCount, (state, { activityHistoryCount }) => ({
    ...state,
    activityHistoryCount
  })),
  on(updateReportData, (state, { reportData, sampleData, reportExportError }) => ({
    ...state,
    reportData,
    sampleData,
    reportExportError: reportExportError
  })),
  on(toggleSelectionByParticipant, state => ({
    ...state,
    isSelectionByParticipant: !state.isSelectionByParticipant
  })),
  on(getTemplateDetailsById, state => ({
    ...state,
    loadingState: LoadStatus.Loading
  })),
  on(getTemplateDetailsByIdFailed, (state, { error }) => ({
    ...state,
    loadingState: LoadStatus.Loaded,
    getDetailsError: error
  })),
  on(saveTemplateDetails, state => ({
    ...state,
    savingState: state.initial ? LoadStatus.Loaded : LoadStatus.Loading
  })),
  on(saveTemplateDetailsSuccess, state => ({
    ...state,
    savingState: LoadStatus.Loaded
  })),
  /** Export Actions */
  on(setTemplateExportData, (state, { exportData }) => ({
    ...state,
    export: {
      ...state.export,
      exportData,
      exportSucceeded: false
    } as TemplateExportState
  })),
  on(openExportListDrawer, state => ({
    ...state,
    export: {
      ...state.export,
      addListDrawerOpened: true
    } as TemplateExportState
  })),
  on(clearExportState, state => ({
    ...state,
    export: {
      ...TemplateExportInitialState,
      exportItineraryComments: state.export.exportItineraryComments,
      selectedList: state.export.selectedList,
      ownershipAnalysisSelectedList: state.export.ownershipAnalysisSelectedList,
      includeOwnershipAnalysis: state.export.includeOwnershipAnalysis,
      exportOwnershipAnalysisPeerSecurity: state.export.exportOwnershipAnalysisPeerSecurity,
      exportOwnershipAnalysisPeerList: state.export.exportOwnershipAnalysisPeerList
    }
  })),
  on(setExportSucceeded, (state, { succeeded }) => ({
    ...state,
    export: {
      ...state.export,
      exportSucceeded: succeeded
    }
  })),
  on(loadSelectedItemsFromStorage, (state, { selectedItems }) => {
    let exportItineraryComments = state.export.exportItineraryComments;

    if (Array.isArray(selectedItems) && selectedItems.length > 0) {
      const itemWithComments = selectedItems.find(item => item.comment || item.commentTitle);
      if (itemWithComments) {
        exportItineraryComments = [
          {
            comment: itemWithComments.comment || '',
            title: itemWithComments.commentTitle || '',
            profileId: '|'
          }
        ];
      }
    }

    const hasActivities = Array.isArray(selectedItems) && selectedItems.some(item => item.activityId && !item.eventId);
    const sortedItems = hasActivities ? sortActivitiesByDate(selectedItems) : selectedItems;

    return {
      ...state,
      export: {
        ...state.export,
        selectedList: sortedItems,
        exportItineraryComments,
        exportSucceeded: false
      }
    };
  }),
  on(loadSelectedTimeFromStorage, (state, { selectedTime }) => ({
    ...state,
    export: {
      ...state.export,
      exportBnyReportTime: new Date(selectedTime),
      exportSucceeded: false
    }
  })),
  on(getListItemsBySelectedListId, state => ({
    ...state,
    export: {
      ...state.export,
      loaderStatus: LoadStatus.Loading
    } as TemplateExportState
  })),
  on(formatListForTemplateExportSuccess, (state, { list }) => {
    const newSelectedList = [...state.export.selectedList, ...list];
    return {
      ...state,
      export: {
        ...state.export,
        loaderStatus: LoadStatus.Loaded,
        selectedList: newSelectedList
      } as TemplateExportState
    };
  }),
  on(addExportItemToList, (state, { itemToAdd }) => {
    const alreadyExists = state.export.selectedList.some(item => {
      // Check by activityId for activities
      if (itemToAdd.activityId) {
        if (item.activityId) {
          return item.activityId === itemToAdd.activityId;
        }
        return false;
      }
      if (itemToAdd.eventId) {
        if (item.eventId) {
          return item.eventId === itemToAdd.eventId;
        }
        return false;
      }
      if (itemToAdd.prId && itemToAdd.crmId) {
        if (item.prId && item.crmId) {
          return item.prId === itemToAdd.prId && item.crmId === itemToAdd.crmId;
        }
        return false;
      }
      return item.displayName === itemToAdd.displayName;
    });
    if (alreadyExists) {
      return state;
    }
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [
          ...state.export.selectedList,
          {
            ...itemToAdd,
            index: state.export.selectedList.length + 1
          }
        ]
      }
    };
  }),

  on(addRemoveItineraryToList, (state, { itemsToAdd }) => {
    let updatedList = [...state.export.selectedList];

    updatedList = updatedList.filter(existingItem => {
      return itemsToAdd.some(itemToAdd => {
        if (itemToAdd.activityId && existingItem.activityId) {
          return itemToAdd.activityId === existingItem.activityId;
        }
        return itemToAdd.displayName === existingItem.displayName;
      });
    });

    const itemsToAddFiltered = itemsToAdd.filter(itemToAdd => {
      return !updatedList.some(item => {
        if (itemToAdd.activityId && item.activityId) {
          return itemToAdd.activityId === item.activityId;
        }
        return itemToAdd.displayName === item.displayName;
      });
    });

    const newItems = itemsToAddFiltered.map((item, index) => ({
      ...item,
      index: updatedList.length + index + 1
    }));

    updatedList = [...updatedList, ...newItems];

    updatedList = sortActivitiesByDate(updatedList);

    updatedList = updatedList.map((item, index) => ({
      ...item,
      index: index + 1
    }));

    return {
      ...state,
      export: {
        ...state.export,
        selectedList: updatedList,
        exportSucceeded: false
      }
    };
  }),
  on(changeSelectedItem, (state, { index }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: state.export.selectedList.map((item, i) => {
          return {
            ...item,
            isSelected: i == index,
            index: i + 1
          };
        }),
        exportSucceeded: false
      }
    };
  }),
  on(changeItinerarySelectedItem, (state, { index }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: state.export.selectedList.map((item, i) => {
          return {
            ...item,
            isSelected: i <= index,
            index: i + 1
          };
        }),
        exportSucceeded: false
      }
    };
  }),
  on(setExportList, (state, { list }) => {
    if (!list || list.length === 0) {
      return {
        ...state,
        export: {
          ...state.export,
          selectedList: [],
          exportSucceeded: false
        }
      };
    }
    const [item] = list;
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [
          {
            ...item,
            isSelected: true,
            index: 1
          }
        ],
        exportSucceeded: false
      }
    };
  }),
  on(removeExportItemFromList, (state, { itemToRemove }) => {
    const updatedList = itemToRemove.id
      ? state.export.selectedList.filter(item => item.id !== itemToRemove.id)
      : state.export.selectedList.filter(item => item.crmId !== itemToRemove.crmId);
    const wasItemSelected = itemToRemove.id
      ? state.export.selectedList.some(item => item.id === itemToRemove.id && item.isSelected)
      : state.export.selectedList.some(item => item.crmId === itemToRemove.crmId && item.isSelected);
    const nextSelectedIndex = calculateNextSelectedIndex(
      state.export.selectedList,
      updatedList,
      itemToRemove,
      wasItemSelected
    );
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: updatedList.map((item, index) => ({
          ...item,
          isSelected: index === nextSelectedIndex
        })),
        exportSucceeded: false
      }
    };
  }),
  /** Comment Section Drawer Actions */
  on(openCommentSectionDrawerForSelectedItem, (state, { selectedItem }) => ({
    ...state,
    export: {
      ...state.export,
      commentSectionDrawerState: {
        isOpened: true,
        selectedListItem: selectedItem
      }
    }
  })),
  on(onCommentsSaveSuccess, (state, { list }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [...list],
        commentSectionDrawerState: {
          isOpened: false,
          selectedListItem: null
        },
        exportSucceeded: false
      }
    };
  }),
  on(onOwnershipAnalysisCommentsSaveSuccess, (state, { list }) => {
    return {
      ...state,
      export: {
        ...state.export,
        ownershipAnalysisSelectedList: [...list],
        commentSectionDrawerState: {
          isOpened: false,
          selectedListItem: null
        },
        exportSucceeded: false
      }
    };
  }),
  on(openPeerOwnershipCommentSectionDrawer, state => ({
    ...state,
    export: {
      ...state.export,
      commentSectionDrawerState: {
        isOpened: true,
        selectedListItem: null
      }
    }
  })),
  on(onPeerOwnershipCommentsSaveSuccess, (state, { comment, commentTitle }) => ({
    ...state,
    export: {
      ...state.export,
      exportPeerOwnershipComment: comment || commentTitle ? { comment, commentTitle } : null,
      peerOwnershipSelectedList: state.export.peerOwnershipSelectedList.map(item =>
        item.id === state.export.exportPeerOwnershipSecurityId
          ? {
              ...item,
              comment,
              commentTitle
            }
          : item
      ),
      commentSectionDrawerState: {
        isOpened: false,
        selectedListItem: null
      },
      exportSucceeded: false
    }
  })),
  on(setOwnershipAnalysisExportList, (state, { list }) => ({
    ...state,
    export: {
      ...state.export,
      ownershipAnalysisSelectedList: list
    }
  })),
  on(setPeerOwnershipExportList, (state, { list }) => ({
    ...state,
    export: {
      ...state.export,
      peerOwnershipSelectedList: list,
      exportPeerOwnershipComment:
        list[0]?.comment || list[0]?.commentTitle
          ? {
              comment: list[0].comment ?? '',
              commentTitle: list[0].commentTitle ?? ''
            }
          : null,
      exportSucceeded: false
    }
  })),
  on(setSelectedPeerOwnershipSecurityId, (state, { selectedSecurityId }) => ({
    ...state,
    export: {
      ...state.export,
      exportPeerOwnershipSecurityId: selectedSecurityId,
      exportSucceeded: false
    } as TemplateExportState
  })),

  /** filter activities drawer actions */
  on(openFilterActivityDrawer, (state, { selectedItem }) => {
    return {
      ...state,
      export: {
        ...state.export,
        filterActivitiesDrawerState: {
          ...state.export.filterActivitiesDrawerState,
          isOpened: true,
          selectedListItem: selectedItem
        }
      }
    };
  }),
  on(onLoadCompanyContacts, state => ({
    ...state,
    export: {
      ...state.export,
      filterActivitiesDrawerState: {
        ...state.export.filterActivitiesDrawerState,
        companyContactLoadStatus: LoadStatus.Loading
      }
    }
  })),
  on(onLoadCompanyContactSuccess, (state, { list }) => ({
    ...state,
    export: {
      ...state.export,
      filterActivitiesDrawerState: {
        ...state.export.filterActivitiesDrawerState,
        companyContactLoadStatus: LoadStatus.Loaded,
        companyContacts: list
      }
    }
  })),
  on(filterActivityApplySuccess, (state, { list }) => {
    return {
      ...state,
      export: {
        ...state.export,
        selectedList: [...list],
        exportSucceeded: false
      }
    };
  }),
  on(clearTemplateDesignerState, state => ({
    ...initialState,
    zoomState: state.zoomState
  })),
  on(reportSectionSelectAll, state => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(true, [...state.reportSections], '', true);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(reportSectionClearAll, state => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSections = panelService.ReportSectionCheckboxToggle(false, [...state.reportSections], '', true);
    return {
      ...state,
      reportSections: modifiedReportSections,
      formattedSections: panelService.ConvertReportSectionsToDraggableList(modifiedReportSections)
    };
  }),
  on(onReportSectionConfigAdditionalDataChanged, (state, { dataSection, additionalConfiguration }) => {
    const panelService = ConfigurationPanelServiceResolver.getServiceForReportType(state.reportTemplate?.reportType);
    const modifiedReportSection: ReportSectionConfigurationWithOrder = {
      ...dataSection,
      config: {
        ...dataSection.config,
        additionalConfiguration: {
          ...dataSection.config.additionalConfiguration,
          ...additionalConfiguration
        }
      } as ReportSectionConfigurationBase
    };
    const { minSize, maxSize } = panelService.GetUpdatedSizes(modifiedReportSection);
    modifiedReportSection.config.minSize = minSize;
    modifiedReportSection.config.maxSize = maxSize;
    const modifiedReportSections = [...state.reportSections].map(section => {
      if (section.config.name === modifiedReportSection.config.name) {
        return modifiedReportSection;
      } else {
        // Check if the section has nestedItems and if any nested item's name matches
        if (section.config.nestedItems) {
          const updatedNestedItems = section.config.nestedItems.map(nested => {
            if (nested.name === modifiedReportSection.config.name && nested.nestedItemSectionConfiguration) {
              return {
                ...nested,
                nestedItemSectionConfiguration: modifiedReportSection
              };
            }
            return nested;
          });
          return {
            ...section,
            config: {
              ...section.config,
              nestedItems: updatedNestedItems
            }
          } as ReportSectionConfigurationWithOrder;
        }
      }
      return section;
    });
    return {
      ...state,
      reportSections: modifiedReportSections,
      selectedReport: {
        ...state.selectedReport,
        reportSectionConfig: modifiedReportSection,
        reportSectionConfigDraggableItem: panelService.PrepareDraggableItemsConfig(modifiedReportSection)
      }
    };
  }),
  on(onEmptySelectionAlert, (state, { show }) => ({
    ...state,
    showEmptySelectionAlert: show
  })),
  on(setCombinedOwnershipEnabled, (state, { enabled }) => ({
    ...state,
    showCombinedOwnership: enabled
  })),
  on(setNotesFeatureEnabled, (state, { enabled }) => ({
    ...state,
    showNotesFeature: enabled
  })),
  on(createConfigurableProfileReport, createBnyManagementReport, createOwnershipAnalysisReport, state => ({
    ...state,
    export: {
      ...state.export,
      exportStatus: LoadStatus.Loading
    }
  })),
  on(
    createProfileReportFinished,
    createBnyManagementReportFinished,
    createConfigurableItineraryReportFinished,
    createOwnershipAnalysisReportFinished,
    state => ({
      ...state,
      export: {
        ...state.export,
        exportStatus: LoadStatus.Loaded
      }
    })
  ),
  on(createConfigurableItineraryReport, state => ({
    ...state,
    export: {
      ...state.export,
      exportStatus: LoadStatus.Loading
    }
  })),
  on(setSelectedBnyDate, (state, { selectedDate }) => ({
    ...state,
    export: {
      ...state.export,
      exportBnyReportTime: selectedDate,
      exportSucceeded: false
    } as TemplateExportState
  })),
  on(setSelectedBnySecurityId, (state, { selectedSecurityId, selectedPrSecurityId }) => ({
    ...state,
    export: {
      ...state.export,
      exportBnySecurityId: selectedSecurityId,
      exportBnyPrSecurityId: selectedPrSecurityId,
      exportSucceeded: false
    } as TemplateExportState
  })),
  on(setSelectedOwnershipAnalysisSecurityId, (state, { selectedSecurityId }) => ({
    ...state,
    export: {
      ...state.export,
      exportOwnershipAnalysisSecurityId: selectedSecurityId,
      exportSucceeded: false
    } as TemplateExportState
  })),
  on(setOwnershipAnalysisPeers, (state, { peerSecurity, peerList }) => ({
    ...state,
    export: {
      ...state.export,
      exportOwnershipAnalysisPeerSecurity: peerSecurity,
      exportOwnershipAnalysisPeerList: peerList
    } as TemplateExportState
  })),
  on(setIncludeOwnershipAnalysis, (state, { includeOwnershipAnalysis }) => ({
    ...state,
    export: {
      ...state.export,
      includeOwnershipAnalysis
    } as TemplateExportState
  })),
  on(setItineraryComments, (state, { itineraryComments }) => {
    const updatedList = state.export.selectedList.map(item => {
      if (itineraryComments.length > 0) {
        const comment = itineraryComments[0];
        return {
          ...item,
          comment: comment.comment,
          commentTitle: comment.title
        };
      }
      return item;
    });

    // Also update reportData to include the new comments so canvas can react
    const updatedReportData = {
      ...state.reportData,
      [ReportViewModelSection.Itinerary_Comments]: itineraryComments
    };

    return {
      ...state,
      reportData: updatedReportData,
      export: {
        ...state.export,
        exportItineraryComments: itineraryComments,
        selectedList: updatedList
      } as TemplateExportState
    };
  }),
  on(changeItineraryTab, (state, { selectedTab }) => ({
    ...state,
    selectedItineraryTab: selectedTab
  }))
);
export function templateDesignerReducer(state: TemplateDesignerState | undefined, action: Action) {
  return reducer(state, action);
}
export const getInitialState = () => initialState;
// Helper function to calculate the next selected index
function calculateNextSelectedIndex(
  originalList: TemplateExportListItemWithComments[],
  updatedList: TemplateExportListItemWithComments[],
  itemToRemove: TemplateExportListItemWithComments,
  wasItemSelected: boolean
) {
  if (wasItemSelected) {
    if (updatedList.length > 0) {
      // Find the index of the item to remove and ensure it doesn't exceed the updated list length
      return Math.min(
        originalList.findIndex(item => item.id === itemToRemove.id),
        updatedList.length - 1
      );
    }
    return -1; // No items left to select
  }
  // If the item wasn't selected, return the index of the currently selected item
  return updatedList.findIndex(item => item.isSelected);
}

// Helper to update parent and nested isActiveTemplate flags concisely
function updateActiveTemplateFlags(
  sections: ReportSectionConfigurationWithOrder[],
  selectedName: string | null | undefined
): ReportSectionConfigurationWithOrder[] {
  return sections.map(section => {
    const isParentSelected = section.config.name === selectedName;
    const updatedNestedItems = section.config.nestedItems?.map(nested => {
      if (!nested.nestedItemSectionConfiguration) return nested;
      return {
        ...nested,
        nestedItemSectionConfiguration: {
          ...nested.nestedItemSectionConfiguration,
          isActiveTemplate: nested.name === selectedName
        }
      };
    });
    return {
      ...section,
      isActiveTemplate: isParentSelected,
      config: {
        ...section.config,
        nestedItems: updatedNestedItems ?? section.config.nestedItems
      }
    } as ReportSectionConfigurationWithOrder;
  });
}
